/* eslint-disable */
import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, ChevronDown } from "lucide-react";
import { useAuth } from "../lib/auth";
import LanguageMenu from "./LanguageMenu";
import Footer from "./Footer";
import FloatingDoshaCTA from "./FloatingDoshaCTA";
import AskDonnyConcierge from "./AskDonnyConcierge";
import LogoutDialog from "./LogoutDialog";
import PageBackBar from "./PageBackBar";
import BRAND from "../constants/brand";

/**
 * DONiTSH!T global header — ecosystem-first navigation.
 *
 * Top-level: Wellness · Relationships · Families & Parenting · Business & Corporate · Shop · Ask Donny · Dosha Test
 * Dropdowns expose the full IA so every link routes somewhere meaningful.
 */
export default function Layout({ children }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileSubOpen, setMobileSubOpen] = useState(null);
  // Phase 7.3.0 UX Polish — Logout confirmation dialog state lives at the Layout
  // level so the AlertDialog is rendered OUTSIDE the user dropdown conditional.
  // Previously, the dialog was nested inside `{accountOpen && (...)}` which meant
  // closing the dropdown unmounted the dialog before it could open.
  const [logoutOpen, setLogoutOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // ---- IA: top-level nav + grouped dropdowns
  const wellnessChildren = [
    { to: "/wellness/mens", label: t("nav.wellness_mens", "Men's Wellness") },
    { to: "/wellness/womens", label: t("nav.wellness_womens", "Women's Wellness") },
    { to: "/wellness/mental", label: t("nav.wellness_mental", "Mental Wellness") },
    { to: "/wellness/nutrition", label: t("nav.wellness_nutrition", "Nutrition") },
    { to: "/wellness/sleep", label: t("nav.wellness_sleep", "Sleep") },
    { to: "/wellness/sports", label: t("nav.wellness_sports", "Sports Performance") },
  ];
  const relationshipsChildren = [
    { to: "/relationships/luv-connect", label: t("nav.rel_luv", "Luv Connect") },
    { to: "/relationships/couples-connect", label: t("nav.rel_couples", "Couples Connect") },
    { to: "/relationships/healthy", label: t("nav.rel_healthy", "Healthy Relationships") },
    { to: "/relationships/communication", label: t("nav.rel_communication", "Communication") },
    { to: "/relationships/dating", label: t("nav.rel_dating", "Dating") },
    { to: "/relationships/wellness", label: t("nav.rel_wellness", "Relationship Wellness") },
  ];
  const familiesChildren = [
    { to: "/families/parenting", label: t("nav.fam_parenting", "Parenting") },
    { to: "/families/wellness", label: t("nav.fam_wellness", "Family Wellness") },
    { to: "/families/teens", label: t("nav.fam_teens", "Teens & Youth") },
  ];
  const businessChildren = [
    { to: "/business/partners", label: t("nav.biz_partners", "Partner Businesses") },
    { to: "/business/corporate", label: t("nav.biz_corporate", "Corporate Wellness") },
    { to: "/business/offers", label: t("nav.biz_offers", "Special Offers") },
    { to: "/marketplace", label: t("nav.biz_marketplace", "Marketplace") },
    { to: "/business/directory", label: t("nav.biz_directory", "Business Directory") },
  ];

  const topNav = [
    { kind: "menu", id: "wellness", label: t("nav.wellness", "Wellness"), to: "/wellness", children: wellnessChildren },
    { kind: "menu", id: "relationships", label: t("nav.relationships", "Relationships"), to: "/relationships", children: relationshipsChildren },
    { kind: "menu", id: "families", label: t("nav.families", "Families & Parenting"), to: "/families", children: familiesChildren },
    { kind: "menu", id: "business", label: t("nav.business", "Business & Corporate"), to: "/business", children: businessChildren },
    { kind: "link", to: "/shop", label: t("nav.shop", "Shop") },
    { kind: "link", to: "/dosha", label: t("nav.dosha", "Dosha Test") },
  ];

  const navRef = useRef(null);
  // Close any open dropdown on route change or outside-click
  useEffect(() => { setOpenMenu(null); setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    function onDocClick(e) {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenMenu(null);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const NavMenu = ({ item }) => {
    const isOpen = openMenu === item.id;
    return (
      <div
        className="relative"
        onMouseEnter={() => setOpenMenu(item.id)}
        onMouseLeave={() => setOpenMenu(null)}
      >
        <button
          type="button"
          onClick={() => setOpenMenu(isOpen ? null : item.id)}
          aria-expanded={isOpen}
          aria-haspopup="menu"
          className={`whitespace-nowrap text-sm px-3 py-2 rounded-md transition-colors inline-flex items-center gap-1 ${
            isOpen ? "text-white bg-white/5" : "text-white/75 hover:text-white hover:bg-white/5"
          }`}
          data-testid={`nav-${item.id}-trigger`}
        >
          {item.label}
          <ChevronDown className={`w-3.5 h-3.5 opacity-60 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </button>
        {isOpen && (
          <div
            role="menu"
            className="absolute left-0 top-full mt-1 min-w-[260px] rounded-xl border border-white/10 bg-[#0F1116]/95 backdrop-blur-md shadow-2xl shadow-black/40 py-1 z-50"
            data-testid={`nav-${item.id}-menu`}
          >
            <Link
              to={item.to}
              className="block px-4 py-2.5 text-sm font-medium gold-text border-b border-white/5 hover:bg-white/5"
              data-testid={`nav-${item.id}-overview`}
            >
              {t("nav.overview", "Overview")}
            </Link>
            {item.children.map((c) => (
              <NavLink
                key={c.to}
                to={c.to}
                className={({ isActive }) =>
                  `block px-4 py-2 text-sm transition-colors ${isActive ? "text-white bg-white/5" : "text-white/80 hover:text-white hover:bg-white/5"}`
                }
                data-testid={`nav-${item.id}-${c.to.split("/").pop()}`}
              >
                {c.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="App">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-black/55 border-b border-white/10" data-testid="site-header">
        <div className="app-container flex items-center gap-3 h-16">
          {/* Logo — always returns home */}
          <Link to="/" className="flex items-center gap-3 group flex-shrink-0" data-testid="brand-link" aria-label={t("nav.brand_home_aria")}>
            <img
              src={BRAND.LOGO}
              alt="DONiTSH!T"
              width="44"
              height="44"
              className="w-11 h-11 rounded-md object-cover ring-1 ring-[#C8A24A]/35 drop-shadow-[0_0_22px_rgba(212,175,55,0.35)] transition-transform duration-200 group-hover:scale-105"
              data-testid="brand-logo"
            />
            <div className="leading-none hidden sm:block">
              <div className="font-display text-base tracking-tight">DONiTSH!T</div>
              <div className="text-[10px] tracking-[0.28em] gold-text uppercase">{t("nav.brand_eyebrow")}</div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav
            ref={navRef}
            className="hidden xl:flex items-center gap-0.5 ms-3 flex-1"
            aria-label="primary"
            data-testid="primary-nav"
          >
            {topNav.map((item) =>
              item.kind === "menu" ? (
                <NavMenu key={item.id} item={item} />
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `whitespace-nowrap text-sm px-3 py-2 rounded-md transition-colors ${
                      item.pill
                        ? isActive
                          ? "text-black bg-[#D4AF37] font-medium"
                          : "text-[#E5C76B] border border-[#C8A24A]/40 hover:bg-[#C8A24A]/15"
                        : isActive
                          ? "text-white bg-white/5"
                          : "text-white/75 hover:text-white hover:bg-white/5"
                    }`}
                  data-testid={`nav-${item.to.replace(/\//g, "-").replace(/^-/, "")}`}
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="ms-auto flex items-center gap-2 flex-shrink-0">
            <LanguageMenu />
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setAccountOpen((s) => !s)}
                  className="btn-ghost flex items-center gap-2"
                  data-testid="user-menu-trigger"
                  aria-label="account menu"
                >
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#E2C77A] to-[#7A5F2A] text-black grid place-items-center text-xs font-bold">
                    {(user.name || user.email || "U")[0]}
                  </div>
                  <ChevronDown className="w-4 h-4 opacity-60" />
                </button>
                {accountOpen && (
                  <div className="absolute end-0 mt-2 w-52 rounded-lg border border-white/10 bg-[#0F1116] shadow-2xl py-1" data-testid="user-menu">
                    <button className="w-full text-start px-3 py-2 text-sm hover:bg-white/5" onClick={() => { setAccountOpen(false); navigate("/profile"); }} data-testid="goto-profile">{t("nav.profile")}</button>
                    {user.is_admin && (
                      <button
                        className="w-full text-start px-3 py-2 text-sm hover:bg-white/5 gold-text"
                        onClick={() => { setAccountOpen(false); navigate("/admin"); }}
                        data-testid="goto-admin"
                      >
                        {t("nav.admin", "Admin")}
                      </button>
                    )}
                    <button
                      className="w-full text-start px-3 py-2 text-sm hover:bg-white/5 text-rose-300"
                      onClick={() => {
                        // Phase 7.3.0 UX fix — close the dropdown first, then open the
                        // confirmation dialog on the next tick. The AlertDialog itself
                        // lives OUTSIDE this conditional block (see end of Layout) so
                        // it does not get unmounted when accountOpen flips to false.
                        setAccountOpen(false);
                        setTimeout(() => setLogoutOpen(true), 0);
                      }}
                      data-testid="logout-btn"
                    >
                      {t("nav.logout")}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" className="btn-ghost hidden sm:inline-block text-sm" data-testid="nav-login">{t("nav.login")}</Link>
                <Link to="/signup" className="btn-secondary text-sm py-2 px-3 hidden sm:inline-block" data-testid="nav-signup">{t("nav.signup")}</Link>
              </>
            )}
            <button className="xl:hidden btn-ghost p-2" onClick={() => setMobileOpen((s) => !s)} aria-label="menu" data-testid="mobile-menu-toggle">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile nav — collapsible submenus */}
        {mobileOpen && (
          <div className="xl:hidden border-t border-white/10 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
            <div className="app-container py-2 flex flex-col">
              {topNav.map((item) =>
                item.kind === "menu" ? (
                  <div key={item.id} className="border-b border-white/5">
                    <button
                      onClick={() => setMobileSubOpen(mobileSubOpen === item.id ? null : item.id)}
                      className="w-full flex items-center justify-between py-3 text-sm font-medium text-white/90"
                      aria-expanded={mobileSubOpen === item.id}
                      data-testid={`mobile-nav-${item.id}-trigger`}
                    >
                      {item.label}
                      <ChevronDown className={`w-4 h-4 transition-transform ${mobileSubOpen === item.id ? "rotate-180" : ""}`} />
                    </button>
                    {mobileSubOpen === item.id && (
                      <div className="pb-2 ps-3" data-testid={`mobile-nav-${item.id}-children`}>
                        <NavLink to={item.to} onClick={() => setMobileOpen(false)} className="block py-2 text-sm gold-text">
                          {t("nav.overview", "Overview")}
                        </NavLink>
                        {item.children.map((c) => (
                          <NavLink
                            key={c.to}
                            to={c.to}
                            onClick={() => setMobileOpen(false)}
                            className="block py-2 text-sm text-white/75 hover:text-white"
                          >
                            {c.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    onClick={() => setMobileOpen(false)}
                    className={`py-3 text-sm border-b border-white/5 ${item.pill ? "gold-text font-medium" : "text-white/80"}`}
                  >
                    {item.label}
                  </NavLink>
                )
              )}
              {!user && (
                <div className="flex gap-2 pt-3">
                  <Link to="/login" onClick={() => setMobileOpen(false)} className="btn-ghost text-sm">{t("nav.login")}</Link>
                  <Link to="/signup" onClick={() => setMobileOpen(false)} className="btn-secondary text-sm py-2 px-3">{t("nav.signup")}</Link>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      <main key={location.pathname}>
        <PageBackBar />
        {children}
      </main>

      {/* Floating Dosha Test bubble — single, sticky, gold */}
      <FloatingDoshaCTA />

      {/* Global Ask Donny concierge — persistent, context-aware */}
      <AskDonnyConcierge />

      <Footer />

      {/* Phase 7.3.0 UX Polish — Logout confirmation dialog, rendered at the
          Layout root so it survives dropdown unmounting. Controlled by
          logoutOpen/setLogoutOpen so it can be opened from anywhere
          (currently: the user menu's "Logout" button). */}
      <LogoutDialog
        controlledOpen={logoutOpen}
        onControlledOpenChange={setLogoutOpen}
        afterLogout={() => navigate("/")}
      />
    </div>
  );
}
